// ════════════════════════════════════════════════════════════════════════
//  Der Entwurf, an dem gerade gearbeitet wird
//
//  Warum ein Kontext: alles unter /lehrgaenge/:slug – Überblick, Lektionen,
//  Grundlagen, Fassungen – arbeitet an DERSELBEN Fassung. Läge der Zustand in
//  einer Seite, wäre er bei jedem Wechsel weg: die Änderung an den Fingern
//  verschwände in dem Moment, in dem man zu den Lektionen hinübergeht.
//
//  Je Lehrgang einer. Der Wechsel des Lehrgangs SOLL den Zustand wegwerfen – es
//  ist ein anderer Vorgang, an einem anderen Entwurf. Dafür sorgt ein `key` auf
//  dem Provider, nicht eine Sonderbehandlung darin.
//
//  Der Zustand liegt bewusst nur im Speicher. Speichern ist eine ausdrückliche
//  Handlung, und `dirty` sagt jederzeit, ob noch etwas offen ist.
//
//  Der Automat selbst liegt in useDraftState.ts; diese Datei ist nur die
//  Verkabelung, die ihn den Baum hinunterreicht.
// ════════════════════════════════════════════════════════════════════════

import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import {
  useDraftState,
  type DraftCurriculum,
  type DraftState,
} from "../hooks/useDraftState.js";

export type DraftContextValue = DraftState;

const Context = createContext<DraftContextValue | null>(null);

/**
 * Reiner Provider: Wert hinein, Kinder heraus.
 *
 * Getrennt vom ladenden `DraftProvider`, damit ein Test einen Bereich mit einem
 * gegebenen Zustand rendern kann, ohne Abfragen nachzubauen. Ohne diese Trennung
 * müsste jeder Komponententest einen Supabase-Client fälschen.
 */
export function DraftValueProvider({
  value,
  children,
}: {
  value: DraftContextValue;
  children: ReactNode;
}) {
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function DraftProvider({
  curriculum,
  children,
}: {
  curriculum: DraftCurriculum | null;
  children: ReactNode;
}) {
  return (
    <DraftValueProvider value={useDraftState(curriculum)}>{children}</DraftValueProvider>
  );
}

export function useDraft(): DraftContextValue {
  const value = useContext(Context);
  if (!value) throw new Error("useDraft needs a DraftProvider");
  return value;
}
